import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { LotSearch } from './lot-search.model';
import { SearchResult } from '../../shared/search-result.model';
import { ColumnFilterComponent, FilterType } from '../../shared/column-filter/column-filter.component';
import { InventoryUnit } from '../../inventory-manager/inventory-unit.model';
import {
    DEPOSIT_INVENTORY,
    LOT_LABEL_PRINTING_PERMISSIONS,
    MANAGE_LOT_PERMISSIONS,
    UPDATE_LOT_BALANCE_PERMISSIONS,
    VIEW_NAMES_PERMISSION,
    VIEW_TRANSACTIONS_PERMISSIONS,
    WITHDRAW_INVENTORY
} from '../../shared/auth/permissions';
import { InventoryContext } from '../../inventory-manager/inventory.context';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';
import { finalize } from 'rxjs/operators';
import { SearchComposite } from '../../shared/model/search-composite';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { GermplasmTreeTableComponent } from '../../shared/tree/germplasm/germplasm-tree-table.component';
import { StudyTreeComponent } from '../../shared/tree/study/study-tree.component';
import { AlertService } from '../../shared/alert/alert.service';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { InventoryService } from '../../shared/inventory/service/inventory.service';
import { ParamContext } from '../../shared/service/param.context';
import { LotDetailPopupService } from '../lot-detail/lot-detail-popup.service';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';
import { LotService } from '../../shared/inventory/service/lot.service';
import { Lot, LotColumnCategory, LotColumnModel } from '../../shared/inventory/model/lot.model';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { preventDefaultOnShiftKey } from '../../field-management/utils';
import { LabelPrintingType } from '../../label-printing/label-printing.component';
import { TranslateService } from '@ngx-translate/core';
import { ITEMS_PER_PAGE, Principal } from '../../shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColumnFilterRadioButtonOption } from '../../shared/column-filter/column-filter-radio-component';

@Component({
    selector: 'jhi-lot',
    templateUrl: './lot.component.html'
})
export class LotComponent implements OnInit, OnDestroy {
    SEED_AMOUNT_UNDEFINED = 'SEED_AMOUNT_UNDEFINED';

    UPDATE_LOT_BALANCE_PERMISSIONS = [...UPDATE_LOT_BALANCE_PERMISSIONS];
    IMPORT_LOTS_PERMISSION = [...MANAGE_LOT_PERMISSIONS, 'IMPORT_LOTS'];
    CREATE_LOTS_PERMISSION = [...MANAGE_LOT_PERMISSIONS, 'CREATE_LOTS'];
    UPDATE_LOTS_PERMISSION = [...MANAGE_LOT_PERMISSIONS, 'UPDATE_LOTS'];
    MERGE_LOTS_PERMISSION = [...MANAGE_LOT_PERMISSIONS, 'MERGE_LOTS'];
    SPLIT_LOT_PERMISSION = [...MANAGE_LOT_PERMISSIONS, 'SPLIT_LOT'];
    CLOSE_LOTS_PERMISSION = [...MANAGE_LOT_PERMISSIONS, 'CLOSE_LOTS'];
    WITHDRAW_INVENTORY_PERMISSION = [...MANAGE_LOT_PERMISSIONS, ...WITHDRAW_INVENTORY];
    DEPOSIT_INVENTORY_PERMISSION = [...MANAGE_LOT_PERMISSIONS, ...DEPOSIT_INVENTORY];
    VIEW_TRANSACTIONS_PERMISSIONS = [...VIEW_TRANSACTIONS_PERMISSIONS];
    LOT_LABEL_PRINTING_PERMISSIONS = [...LOT_LABEL_PRINTING_PERMISSIONS];
    ACTION_BUTTON_PERMISSIONS = [...MANAGE_LOT_PERMISSIONS,
        'IMPORT_LOTS',
        'CREATE_LOTS',
        'UPDATE_LOTS',
        'MERGE_LOTS',
        'SPLIT_LOT',
        'CLOSE_LOTS',
        ...WITHDRAW_INVENTORY,
        ...DEPOSIT_INVENTORY,
        ...LOT_LABEL_PRINTING_PERMISSIONS];

    TOOLTIP_MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    VIEW_NAMES_PERMISSION = VIEW_NAMES_PERMISSION;
    lots: Lot[];
    error: any;
    currentSearch: string;
    links: any;
    filteredItems: any;
    queryCount: any;
    itemsPerPage: any;
    page: any;
    predicate: any;
    previousPage: any;
    reverse: boolean;
    searchResult: SearchResult;

    isLoading: boolean;
    shownAttributes = {};
    lastClickIndex: any;

    subscriptionsManager = new SubscriptionsManager();

    @ViewChild(ColumnFilterComponent)
        columnFilterComponent: ColumnFilterComponent;

    get request() {
        return this.inventoryContext.lotSearchRequest;
    }

    set request(request) {
        this.inventoryContext.lotSearchRequest = request;
    }

    get filters() {
        return this.inventoryContext.lotFilters;
    }

    set filters(filters) {
        this.inventoryContext.lotFilters = filters;
    }

    get hiddenColumns() {
        return this.inventoryContext.lotHiddenColumns;
    }

    set hiddenColumns(hiddenColumns) {
        this.inventoryContext.lotHiddenColumns = hiddenColumns;
    }

    units: Promise<InventoryUnit[]>;
    selectedItems: any[] = [];
    allItemsPerPages = false;

    private getInitialFilters(units) {
        return [
            {
                key: 'status', name: 'column-filter.status', default: true, defaultValue: 0, type: FilterType.RADIOBUTTON,
                options: this.getStatusFilterOptions()
            },
            { key: 'lotIds', name: 'column-filter.lot.id', placeholder: 'column-filter.comma.separated.values.placeholder', default: true, type: FilterType.LIST },
            {
                key: 'lotUUIDs', name: 'column-filter.lot.uid', type: FilterType.TEXT, placeholder: 'column-filter.match.text.placeholder',
                transform(req) {
                    req[this.key] = [this.value];
                },
            },
            { key: 'stockId', name: 'column-filter.stock.id', placeholder: 'column-filter.starts.with.placeholder', type: FilterType.TEXT },
            { key: 'gids', name: 'column-filter.gid', placeholder: 'column-filter.comma.separated.values.placeholder', type: FilterType.LIST },
            {
                key: 'germplasmListIds', name: 'column-filter.germplasm.list', type: FilterType.MODAL,
                open(modal, request) {
                    return new Promise<void>((resolve) => {
                        modal.open(GermplasmTreeTableComponent as Component, { size: 'lg', backdrop: 'static' })
                            .result.then((germplasmList) => {
                                if (germplasmList) {
                                    this.value = germplasmList.map((list) => list.name);
                                    request[this.key] = germplasmList.map((list) => list.id);
                                }
                                resolve();
                            }, () => {
                            });
                    });
                }
            },
            {
                key: 'plantingStudyIds', name: 'column-filter.study.of.use', type: FilterType.MODAL,
                open(modal, request) {
                    return new Promise<void>((resolve) => {
                        modal.open(StudyTreeComponent as Component, { windowClass: 'modal-extra-small', backdrop: 'static' })
                            .result.then((studyIds) => {
                                if (studyIds) {
                                    this.value = studyIds.map((list) => list.name);
                                    request[this.key] = studyIds.map((list) => list.id);
                                }
                                resolve();
                            }, () => {
                            });
                    });
                }
            },
            {
                key: 'originStudyIds', name: 'column-filter.study.of.origin', type: FilterType.MODAL,
                open(modal, request) {
                    return new Promise<void>((resolve) => {
                        modal.open(StudyTreeComponent as Component, { windowClass: 'modal-extra-small', backdrop: 'static' })
                            .result.then((studyIds) => {
                                if (studyIds) {
                                    this.value = studyIds.map((list) => list.name);
                                    request[this.key] = studyIds.map((list) => list.id);
                                }
                                resolve();
                            }, () => {
                            });
                    });
                }
            },
            { key: 'mgids', name: 'column-filter.group.id', placeholder: 'column-filter.comma.separated.values.placeholder', type: FilterType.LIST },
            { key: 'designation', name: 'column-filter.designation', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'locationNameContainsString', name: 'column-filter.storage.location', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'unitIds', name: 'column-filter.units', type: FilterType.CHECKLIST,
                options: units
            },
            {
                key: 'actualBalance', name: 'column-filter.actual.balance', type: FilterType.NUMBER,
                fromKey: 'minActualBalance',
                toKey: 'maxActualBalance',
                transform(req) {
                    ColumnFilterComponent.transformMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.min = req[this.fromKey];
                    this.max = req[this.toKey];
                }
            },
            {
                key: 'availableBalance', name: 'column-filter.available', type: FilterType.NUMBER,
                fromKey: 'minActualBalance',
                toKey: 'maxActualBalance',
                transform(req) {
                    ColumnFilterComponent.transformMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.min = req[this.fromKey];
                    this.max = req[this.toKey];
                }
            },
            {
                key: 'reservedTotal', name: 'column-filter.reserved', type: FilterType.NUMBER,
                fromKey: 'minReservedTotal',
                toKey: 'maxReservedTotal',
                transform(req) {
                    ColumnFilterComponent.transformMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.min = req[this.fromKey];
                    this.max = req[this.toKey];
                }
            },
            {
                key: 'withdrawalTotal', name: 'column-filter.total.withdrawals', type: FilterType.NUMBER,
                fromKey: 'minWithdrawalTotal',
                toKey: 'maxWithdrawalTotal',
                transform(req) {
                    ColumnFilterComponent.transformMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.min = req[this.fromKey];
                    this.max = req[this.toKey];
                }
            },
            {
                key: 'pendingDepositsTotal', name: 'column-filter.pending.deposits', type: FilterType.NUMBER,
                fromKey: 'minPendingDepositsTotal',
                toKey: 'maxPendingDepositsTotal',
                transform(req) {
                    ColumnFilterComponent.transformMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.min = req[this.fromKey];
                    this.max = req[this.toKey];
                }
            },
            { key: 'notesContainsString', name: 'column-filter.notes', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'createdByUsername', name: 'column-filter.username', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'createdDate', name: 'column-filter.creation.date', type: FilterType.DATE,
                fromKey: 'createdDateFrom',
                toKey: 'createdDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetDateFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            {
                key: 'lastDepositDate', name: 'column-filter.last.deposit.date', type: FilterType.DATE,
                fromKey: 'lastDepositDateFrom',
                toKey: 'lastDepositDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetDateFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            {
                key: 'lastWithdrawalDate', name: 'column-filter.last.withdrawal.date', type: FilterType.DATE,
                fromKey: 'lastWithdrawalDateFrom',
                toKey: 'lastWithdrawalDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetDateFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            { key: 'sourceUid', name: 'column-filter.source.lot.uid', type: FilterType.TEXT, placeholder: 'column-filter.match.text.placeholder' },
            { key: 'sourceStockId', name: 'column-filter.source.stock.id', placeholder: 'column-filter.starts.with.placeholder', type: FilterType.TEXT },
            {
                key: 'attributes', name: 'column-filter.attributes', placeholder: 'column-filter.attributes.placeholder', type: FilterType.LOT_ATTRIBUTES, attributes: [],
                transform(req) {
                    if (req.currentLotAttributeIds) {
                        if (!req.attributes) {
                            req.attributes = {};
                        }
                        req.currentLotAttributeIds.forEach((id) => req.attributes[id] = null);
                    }
                    ColumnFilterComponent.transformLotAttributesFilter(this, req);
                },
                reset(req) {
                    if (req.currentLotAttributeIds) {
                        if (!req.attributes) {
                            req.attributes = {};
                        }
                        req.currentLotAttributeIds.forEach((id) => req.attributes[id] = null);
                    }
                    ColumnFilterComponent.resetAttributesFilter(this, req);
                },
            },
        ];
    }

    constructor(
        private jhiLanguageService: JhiLanguageService,
        private translateService: TranslateService,
        private lotService: LotService,
        private alertService: AlertService,
        private principal: Principal,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private eventManager: JhiEventManager,
        private inventoryService: InventoryService,
        private inventoryContext: InventoryContext,
        private modalService: NgbModal,
        private paramContext: ParamContext,
        public lotDetailPopupService: LotDetailPopupService,
        public germplasmDetailsPopupService: GermplasmDetailsPopupService
    ) {
        this.paramContext.readParams();
        this.itemsPerPage = ITEMS_PER_PAGE;
        this.subscriptionsManager.add = this.activatedRoute.data.subscribe((data) => {
            this.page = data.pagingParams.page;
            this.previousPage = data.pagingParams.page;
            this.reverse = data.pagingParams.ascending;
            this.predicate = data.pagingParams.predicate;
        });
        this.currentSearch = this.activatedRoute.snapshot && this.activatedRoute.snapshot.params['search'] ?
            this.activatedRoute.snapshot.params['search'] : '';

        this.units = this.inventoryService.queryUnits().toPromise();

        if (!this.filters) {
            this.filters = this.getInitialFilters(this.getUnits());
            if (!this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSION)) {
                this.filters = this.filters.filter((filter) => {
                    return filter.key !== 'designation';
                });
            }
            this.request.status = 0;
            this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
        }
        this.searchResult = new SearchResult('');
    }

    /**
     * Returns a copy of the units
     */
    private getUnits() {
        return new Promise((resolve) => {
            this.units.then((units: InventoryUnit[]) => {
                resolve(units.map((unit) => <InventoryUnit>(unit)));
            });
        });
    }

    private async getStatusFilterOptions(): Promise<ColumnFilterRadioButtonOption[]> {
        await this.translateService.get('column-filter.active.option').toPromise();
        return new Promise<ColumnFilterRadioButtonOption[]>((resolve, reject) => {
            resolve([new ColumnFilterRadioButtonOption(0, 'column-filter.active.option'),
                new ColumnFilterRadioButtonOption(1, 'column-filter.closed.option')]);
        });
    }

    search(request: LotSearch): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!this.searchResult.searchResultDbId) {
                this.subscriptionsManager.add = this.lotService.search(request).subscribe((response) => {
                    this.searchResult.searchResultDbId = response;
                    resolve(this.searchResult.searchResultDbId);
                }, (error) => reject(error));
                this.page = 1;
            } else {
                resolve(this.searchResult.searchResultDbId);
            }
        });
    }

    loadAll(request: LotSearch) {
        this.isLoading = true;
        this.search(request).then((searchId) => {
            this.subscriptionsManager.add = this.lotService.getSearchResults({
                searchRequestId: searchId,
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.sort()
            }).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe(
                (res: HttpResponse<Lot[]>) => this.onSuccess(res.body, res.headers)
            );
        });
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    clear() {
        this.page = 0;
        this.currentSearch = '';
        this.router.navigate(['./', {
            page: this.page,
            sort: this.predicate + ',' + (this.reverse ? 'asc' : 'desc')
        }], { relativeTo: this.activatedRoute });
        this.loadAll(this.request);
    }

    ngOnInit() {
        this.resetRequestAttributes();
        this.loadAll(this.request);
        this.registerChangeInLots();
        this.hiddenColumns['lotId'] = true;
        this.hiddenColumns['lotUUID'] = true;
        this.hiddenColumns['sourceUid'] = true;
        this.hiddenColumns['sourceStockId'] = true;
    }

    resetRequestAttributes() {
        this.request.addedColumnsPropertyIds = [];
        this.request.attributes = null;
        this.request.currentLotAttributeIds = [];
    }

    ngOnDestroy() {
        this.subscriptionsManager.unsubscribeAll();
    }

    trackId(index: number, item: Lot) {
        return item.lotId;
    }

    registerChangeInLots() {
        this.subscriptionsManager.add = this.eventManager.subscribe('columnFiltersChanged', (event) => {
            if (event && event.content && event.content.filterBy) {
                this.filterBy(event.content.filterBy);
            }
            this.resetTable();
        });

        this.subscriptionsManager.add = this.eventManager.subscribe('lotAttributeFiltersChanged', (event) => {
            const columns = event.content;
            // to display filtered attribute's column header
            columns.forEach((column) => this.shownAttributes[column.id] = column.alias ? `${column.alias} (${column.name}) ` : column.name);
        });

        this.subscriptionsManager.add = this.eventManager.subscribe('hasAttributeChange', (event) => {
            this.loadAll(this.request);
        });
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.allItemsPerPages = false;
        this.selectedItems = [];
        this.loadAll(this.request);
    }

    sort() {
        const result = [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
        if (this.predicate !== 'lotId') {
            result.push('lotId');
        }
        return result;
    }

    private onSuccess(data, headers) {
        this.filteredItems = headers.get('X-Filtered-Count');
        this.queryCount = this.filteredItems;
        this.lots = data;
    }

    isSelected(lot: Lot) {
        return lot && this.selectedItems.length > 0 && this.selectedItems.find((item) => item === lot.lotUUID);
    }

    onSelectAllPage() {
        const allPageSelected = this.isAllPageSelected();
        const lotCurrentPage = this.lots.map((lot) => lot.lotUUID);
        if (allPageSelected) {
            this.selectedItems = this.selectedItems.filter((item) =>
                lotCurrentPage.indexOf(item) === -1);
        } else {
            this.selectedItems = lotCurrentPage.filter((item) =>
                this.selectedItems.indexOf(item) === -1
            ).concat(this.selectedItems);
        }
    }

    onSelectAllPages(selectAllItems) {
        this.allItemsPerPages = !selectAllItems;
        if (this.allItemsPerPages) {
            this.selectedItems = [];
        }
    }

    toggleSelect($event, index, lot: Lot, checkbox = false) {
        if (this.allItemsPerPages) {
            return;
        }
        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.selectedItems = [];
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.lots.slice(min, max);
        } else {
            items = [lot];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.find((item) => item === lot.lotUUID);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems = this.selectedItems.filter((item) => item !== lot.lotUUID);
            } else {
                this.selectedItems.push(item.lotUUID);
            }
        }
    }

    isAllPageSelected() {
        return this.lots.length > 0 && !this.lots.some((lot) => this.selectedItems.indexOf(lot.lotUUID) === -1);
    }

    private validateSelection() {
        if (this.lots.length === 0 || (!this.allItemsPerPages && this.selectedItems.length === 0)) {
            this.alertService.error('inventoryManagerApp.lot.selection.empty');
            return false;
        }
        return true;
    }

    printLabels() {
        if (this.lots.length === 0) {
            this.alertService.error('inventoryManagerApp.lot.selection.filter.no.result');
            return;
        }

        const input = {
            selectedLotUUIDs: this.selectedItems,
            searchRequestId: this.searchResult.searchResultDbId
        };

        this.lotService.validateLabelsInfoInputData(input).toPromise().then(() => {
            this.paramContext.resetQueryParams('.').then(() => {
                this.router.navigate(['/', 'label-printing-main'], {
                    queryParams: {
                        cropName: this.paramContext.cropName,
                        programUUID: this.paramContext.programUUID,
                        printingLabelType: LabelPrintingType.LOT,
                        openedDirectlyFromNewStudyEditor: false,
                        selectedLotUUIDs: this.selectedItems,
                        searchRequestId: this.searchResult.searchResultDbId
                    },
                    queryParamsHandling: 'merge',
                    onSameUrlNavigation: 'reload'
                }).then(() => {
                });
            });
        });
    }

    openWithdrawalModal() {
        if (!this.validateSelection()) {
            return;
        }

        const searchComposite: SearchComposite<any, any> = new SearchComposite();

        if (this.allItemsPerPages) {
            searchComposite.searchRequest = this.searchResult.searchResultDbId;
            this.inventoryContext.itemIds = undefined;
        } else {
            searchComposite.itemIds = this.selectedItems;
            this.inventoryContext.itemIds = this.selectedItems;
        }

        this.subscriptionsManager.add = this.lotService.getSearchMetaData(searchComposite).subscribe(
            (res: HttpResponse<any>) => {
                if (res.body.lotsCountByUnitName.NULL_VALUES) {
                    this.alertService.error('inventoryManagerApp.lot.withdraw.validation.null-values', {
                        param: res.body.lotsCountByUnitName.NULL_VALUES
                    });
                    return;
                }

                if (res.body.lotsCountByUnitName.SEED_AMOUNT_UNDEFINED) {
                    this.alertService.error('inventoryManagerApp.lot.withdraw.validation.seed-amount-undefined', {
                        param: this.SEED_AMOUNT_UNDEFINED
                    });
                    return;
                }

                this.inventoryContext.lotsCountByUnitName = res.body.lotsCountByUnitName;
                this.inventoryContext.itemIds = this.selectedItems;
                this.router.navigate(['/',
                    {
                        outlets: { popup: 'lot-withdrawal' },
                    }], {
                    replaceUrl: true,
                    queryParams:
                        {
                            searchResultDbId: searchComposite.searchRequest
                        }, queryParamsHandling: 'merge'
                });
            }
        );
    }

    openUpdateModal() {
        if (!this.validateSelection()) {
            return;
        }
        let searchResultDbId = null;
        if (this.allItemsPerPages) {
            searchResultDbId = this.searchResult.searchResultDbId;
            this.inventoryContext.itemIds = undefined;
        } else {
            this.inventoryContext.itemIds = this.selectedItems;
        }

        this.inventoryContext.itemCount = this.allItemsPerPages ? this.filteredItems : this.selectedItems.length;

        this.router.navigate(['/', { outlets: { popup: 'lot-update' }, }], {
            replaceUrl: true,
            queryParams: { searchResultDbId }, queryParamsHandling: 'merge'
        });
    }

    openMergeLotsModal() {
        if (!this.allItemsPerPages && this.selectedItems.length < 2) {
            this.alertService.error('inventoryManagerApp.lot.merge.validation.at-least-two');
            return;
        }

        const searchComposite: SearchComposite<any, any> = new SearchComposite();
        if (this.allItemsPerPages) {
            searchComposite.searchRequest = this.searchResult.searchResultDbId;
            this.inventoryContext.itemIds = undefined;
        } else {
            searchComposite.itemIds = this.selectedItems;
            this.inventoryContext.itemIds = this.selectedItems;
        }

        this.subscriptionsManager.add = this.lotService.getSearchMetaData(searchComposite).subscribe(
            (res: HttpResponse<any>) => {
                if (res.body.lotsCountByUnitName.NULL_VALUES) {
                    this.alertService.error('inventoryManagerApp.lot.action.validation.null-values', {
                        param: res.body.lotsCountByUnitName.NULL_VALUES
                    });
                    return;

                }
                if (res.body.lotsCountByUnitName.SEED_AMOUNT_UNDEFINED) {
                    this.alertService.error('inventoryManagerApp.lot.merge.validation.seed-amount-undefined', {
                        param: this.SEED_AMOUNT_UNDEFINED
                    });
                    return;
                }
                this.inventoryContext.itemIds = this.selectedItems;
                this.router.navigate(['/', { outlets: { popup: 'lot-merge' }, }], {
                    replaceUrl: true,
                    queryParams: { searchResultDbId: searchComposite.searchRequest }, queryParamsHandling: 'merge'
                });
            }
        );
    }

    openSplitLotModal() {
        if (this.lots.length === 0 || this.allItemsPerPages || this.selectedItems.length <= 0) {
            this.alertService.error('inventoryManagerApp.lot.split.validation.select-only-one');
            return;
        }
        const searchComposite: SearchComposite<any, any> = new SearchComposite();
        searchComposite.itemIds = this.selectedItems;

        this.subscriptionsManager.add = this.lotService.getSearchMetaData(searchComposite).subscribe(
            (res: HttpResponse<any>) => {
                if (res.body.lotsCountByUnitName.NULL_VALUES) {
                    this.alertService.error('inventoryManagerApp.lot.action.validation.null-values', {
                        param: res.body.lotsCountByUnitName.NULL_VALUES
                    });
                    return;

                }
                if (res.body.lotsCountByUnitName.SEED_AMOUNT_UNDEFINED) {
                    this.alertService.error('inventoryManagerApp.lot.split.validation.seed-amount-undefined', {
                        param: this.SEED_AMOUNT_UNDEFINED
                    });
                    return;
                }

                if (Object.keys(res.body.lotsCountByUnitName).length > 1) {
                    this.alertService.error('inventoryManagerApp.lot.split.validation.unit-inconsistent');
                    return;
                }

                const selectedItem = this.selectedItems;
                this.router.navigate(['/', { outlets: { popup: 'lot-split' }, }], {
                    replaceUrl: true,
                    queryParams: { selectedItem },
                    queryParamsHandling: 'merge'
                });
            }
        );
    }

    openCloseLotsModal() {
        if (!this.validateSelection()) {
            return;
        }

        const searchComposite: SearchComposite<any, any> = new SearchComposite();
        if (this.allItemsPerPages) {
            searchComposite.searchRequest = this.searchResult.searchResultDbId;
        } else {
            searchComposite.itemIds = this.selectedItems;
        }

        const numOfLotsToClose = this.allItemsPerPages ? this.filteredItems : this.selectedItems.length;
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('inventoryManagerApp.lot.close.confirm.message', { param: numOfLotsToClose });
        confirmModalRef.componentInstance.title = this.translateService.instant('inventoryManagerApp.lot.close.confirm.title');
        confirmModalRef.result.then(() => {
            this.subscriptionsManager.add = this.lotService.closeLot(searchComposite).subscribe(
                (res: HttpResponse<any>) => {
                    return this.onSaveSuccess('inventoryManagerApp.lot.close.success');
                }
            );
        });
    }

    private onSaveSuccess(message: any) {
        this.alertService.success(message);
        this.resetTable();
    }

    openDepositModal() {
        if (!this.validateSelection()) {
            return;
        }

        const searchComposite: SearchComposite<any, any> = new SearchComposite();
        if (this.allItemsPerPages) {
            searchComposite.searchRequest = this.searchResult.searchResultDbId;
            this.inventoryContext.itemIds = undefined;
        } else {
            searchComposite.itemIds = this.selectedItems;
            this.inventoryContext.itemIds = this.selectedItems;
        }

        this.subscriptionsManager.add = this.lotService.getSearchMetaData(searchComposite).subscribe(
            (res: HttpResponse<any>) => {
                if (res.body.lotsCountByUnitName.NULL_VALUES) {
                    this.alertService.error('inventoryManagerApp.lot.deposit.validation.null-values', {
                        param: res.body.lotsCountByUnitName.NULL_VALUES
                    });
                    return;

                }
                if (res.body.lotsCountByUnitName.SEED_AMOUNT_UNDEFINED) {
                    this.alertService.error('inventoryManagerApp.lot.deposit.validation.seed-amount-undefined', {
                        param: this.SEED_AMOUNT_UNDEFINED
                    });
                    return;
                }

                this.inventoryContext.lotsCountByUnitName = res.body.lotsCountByUnitName;
                this.inventoryContext.itemIds = this.selectedItems;
                this.router.navigate(['/', { outlets: { popup: 'lot-deposit' }, }], {
                    replaceUrl: true,
                    queryParams: { searchResultDbId: searchComposite.searchRequest }, queryParamsHandling: 'merge'
                });
            }
        );
    }

    openAdjustLotBalanceModal() {
        if (!this.validateSelection()) {
            return;
        }

        const searchComposite: SearchComposite<any, any> = new SearchComposite();
        if (this.allItemsPerPages) {
            searchComposite.searchRequest = this.searchResult.searchResultDbId;
            this.inventoryContext.itemIds = undefined;
        } else {
            searchComposite.itemIds = this.selectedItems;
            this.inventoryContext.itemIds = this.selectedItems;
        }

        this.subscriptionsManager.add = this.lotService.getSearchMetaData(searchComposite).subscribe(
            (res: HttpResponse<any>) => {
                if (res.body.lotsCountByUnitName.NULL_VALUES) {
                    this.alertService.error('inventoryManagerApp.lot.action.validation.null-values', {
                        param: res.body.lotsCountByUnitName.NULL_VALUES
                    });
                    return;

                }
                if (res.body.lotsCountByUnitName.SEED_AMOUNT_UNDEFINED) {
                    this.alertService.error('inventoryManagerApp.lot.update.balance.validation.seed-amount-undefined', {
                        param: this.SEED_AMOUNT_UNDEFINED
                    });
                    return;
                }
                this.inventoryContext.itemIds = this.selectedItems;
                this.router.navigate(['/', { outlets: { popup: 'lot-balance-adjustment' }, }], {
                    replaceUrl: true,
                    queryParams: { searchResultDbId: searchComposite.searchRequest }, queryParamsHandling: 'merge'
                });
            }
        );
    }

    openImportDepositsModal() {
        this.router.navigate(['/', { outlets: { popup: 'lot-import-deposit' }, }], {
            replaceUrl: true,
            queryParamsHandling: 'merge'
        });
    }

    onColumnsSelected(columns: LotColumnModel[]) {
        this.hiddenColumns = {};
        const newAttributeIds: number[] = [];
        columns.forEach((column) => {
            if (column.category === LotColumnCategory.STATIC) {
                this.hiddenColumns[column.id] = !column.selected;
            } else if (column.category === LotColumnCategory.ATTRIBUTE && column.selected) {
                this.shownAttributes[column.id] = column.displayName;
                newAttributeIds.push(parseInt(column.id, 10));
            }
        });

        if (newAttributeIds.sort().toString() !== this.request.currentLotAttributeIds.toString()) {
            this.request.currentLotAttributeIds = newAttributeIds.sort();
            this.request.attributes = {};
            this.request.currentLotAttributeIds.forEach((id) => this.request.attributes[id] = null);
            this.searchResult.searchResultDbId = null;
            this.addFilteredAttributeColumnsToRequest();

            this.resetTable();
        }

    }

    private addFilteredAttributeColumnsToRequest() {
        this.getAttributeFilters().forEach((attr) => this.request.attributes[attr.id] = attr.value);
    }

    private getAttributeFilters() {
        const attributeObj = this.filters.filter((f) => f.key === 'attributes');
        if (attributeObj.length > 0 && attributeObj[0].attributes && attributeObj[0].attributes.length > 0) {
            return attributeObj[0].attributes;
        }
        return [];
    }

    /**
     * Filter by different fields.
     * - Some filters may have a custom filtering logic
     * - Will clear all existing filters
     * @param filterBy map with key value filters
     */
    private filterBy(filterBy: { [p: string]: any }) {
        if (!filterBy) {
            return;
        }
        const entries = Object.entries(filterBy);
        if (entries.length === 0) {
            return;
        }
        this.resetFilters();
        entries.forEach(([key, value]) => {
            switch (key) {
            case 'lotUUIDs': {
                const filterLotUUIDs = this.filters.find((f) => f.key === 'lotUUIDs');
                filterLotUUIDs.added = true;
                this.request.lotUUIDs = value;
                this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
            }
            }
        });
    }

    private resetFilters() {
        this.filters = this.getInitialFilters(this.getUnits());
        this.request = new LotSearch();
        this.columnFilterComponent.removeAllAttributesColumn();
        this.request.addedColumnsPropertyIds = [];
        this.searchResult = new SearchResult('');
    }

    clearSelected() {
        this.selectedItems = [];
        this.allItemsPerPages = true;
    }

    onPageSizeChange($event: Event) {
        this.loadAll(this.request);
    }

    protected readonly preventDefaultOnShiftKey = preventDefaultOnShiftKey;
}
